import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { Select, TextInput } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useGetIntegrationByName from '@ubeya/shared/hooks/account/useGetIntegrationByName';
import useXeroContactsForInvoicing from '@ubeya/shared/hooks/account/useXeroContactsForInvoicing';
import AddXeroInvoicingContactModal from '../AddXeroInvoicingContactModal';

const ADD_NEW_CLIENT_VALUE = 'ADD_NEW_XERO_CLIENT';

const FIELD_NAME = 'invoicing.registeredNameForInvoicing';

const fieldSubscription = { value: true, touched: true, error: true };

/** Drops non-objects and rows without contactId; coerces label/value for Select. */
function normalizeXeroContactsForSelect(rawContacts) {
  if (!Array.isArray(rawContacts)) {
    return [];
  }
  return rawContacts.flatMap((contact) => {
    if (contact && typeof contact === 'object' && !Array.isArray(contact) && !!contact.contactId) {
      return [
        {
          name: contact.name === undefined || contact.name === null ? '' : String(contact.name),
          contactId: String(contact.contactId)
        }
      ];
    }
    return [];
  });
}

const RegisteredNameForInvoicingField = () => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const xeroIntegration = useGetIntegrationByName('xero');
  const xeroContactsQueryEnabled = !!(accountId && xeroIntegration?.id);
  const { contacts, fetchFailed, refetch } = useXeroContactsForInvoicing(accountId, xeroContactsQueryEnabled);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const { input, meta } = useField(FIELD_NAME, { subscription: fieldSubscription });

  const savedName = input.value ?? '';

  const xeroContacts = useMemo(() => normalizeXeroContactsForSelect(contacts), [contacts]);

  // duplicate display names stay distinct rows
  const selectOptions = useMemo(
    () => [
      ...xeroContacts.map((contact) => ({ label: contact.name, value: contact.contactId })),
      { label: t('addNewClient'), value: ADD_NEW_CLIENT_VALUE }
    ],
    [xeroContacts, t]
  );

  const selectValue = useMemo(() => {
    const matchedBySavedName = xeroContacts.filter((contact) => contact.name === savedName);
    return matchedBySavedName.length >= 1 ? matchedBySavedName[0].contactId : savedName;
  }, [xeroContacts, savedName]);

  const displayInitialValueNotInOptions = useMemo(() => {
    const matchedBySavedName = xeroContacts.filter((contact) => contact.name === savedName);
    const optionIds = new Set(xeroContacts.map((contact) => contact.contactId));
    return savedName &&
      savedName !== ADD_NEW_CLIENT_VALUE &&
      matchedBySavedName.length === 0 &&
      !optionIds.has(savedName)
      ? [{ label: savedName, value: savedName }]
      : [];
  }, [xeroContacts, savedName]);

  const handleSelectChange = useCallback(
    (selectedValue) => {
      if (selectedValue === ADD_NEW_CLIENT_VALUE) {
        setAddModalOpen(true);
      } else {
        const selectedId = selectedValue === undefined || selectedValue === null ? '' : String(selectedValue);
        const matchedContact = xeroContacts.find((contact) => contact.contactId === selectedId);
        input.onChange(matchedContact?.name ?? selectedId ?? '');
      }
    },
    [xeroContacts, input]
  );

  const handleAddModalSuccess = useCallback(
    (name) => {
      input.onChange(name ?? '');
      refetch();
    },
    [input, refetch]
  );

  const handleCloseAddModal = useCallback(() => {
    setAddModalOpen(false);
  }, []);

  // when Xero is connected and fetch succeeds, the Select shows immediately
  const usePlainInputOnly = !xeroIntegration?.id || fetchFailed;

  if (usePlainInputOnly) {
    return (
      <TextInput
        {...input}
        title={t('invoicingSettings__registeredNameForInvoicing')}
        autoFocus
        error={meta.touched && meta.error}
      />
    );
  }

  return (
    <>
      <Select
        title={t('invoicingSettings__registeredNameForInvoicing')}
        value={selectValue}
        onChange={handleSelectChange}
        onBlur={input.onBlur}
        options={selectOptions}
        isMulti={false}
        searchable
        displayInitialValueNotInOptions={displayInitialValueNotInOptions}
        error={meta.touched && meta.error}
      />
      {addModalOpen && (
        <AddXeroInvoicingContactModal
          accountId={accountId}
          onClose={handleCloseAddModal}
          onSuccess={handleAddModalSuccess}
        />
      )}
    </>
  );
};

export default RegisteredNameForInvoicingField;
